import { NEW_ANALYTICS } from '@kontentino/kontentino-constants/Features';
import routes from 'constants/routes';
import { UserPermission } from 'constants/userPermission';
import { User } from 'types/User';
import OldApp from 'config/oldApp';
import i18n from 'app/utils/i18n';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faChartPieSimple } from '@fortawesome/pro-regular-svg-icons/faChartPieSimple';
import { faChartSimple } from '@fortawesome/pro-regular-svg-icons/faChartSimple';
import { faChartTreeMap } from '@fortawesome/pro-regular-svg-icons/faChartTreeMap';
import { faFlag } from '@fortawesome/pro-regular-svg-icons/faFlag';
import { faFolderImage } from '@fortawesome/pro-regular-svg-icons/faFolderImage';
import { faGrid2 } from '@fortawesome/pro-regular-svg-icons/faGrid2';
import { faHouse } from '@fortawesome/pro-regular-svg-icons/faHouse';
import { faTable } from '@fortawesome/pro-solid-svg-icons/faTable';
import { isViewRestrictedForPageType } from 'app/modules/insights/utils/isViewRestrictedForPageType';
import { isIncludedInInsightsLiteExperiment } from 'app/modules/insightsLite/utils/isIncludedInInsightsLiteExperiment';
import { canAccessNewAnalytics } from 'app/modules/analytics/config/analyticsConfig';
import { ReactNode } from 'react';
import { AccountStore } from 'modules/account/accountReducer';
import { faFiles } from '@fortawesome/pro-regular-svg-icons/faFiles';
import { faFilePlus } from '@fortawesome/pro-regular-svg-icons/faFilePlus';
import { faSwatchbook } from '@fortawesome/pro-regular-svg-icons/faSwatchbook';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faFileChartPie } from '@fortawesome/pro-regular-svg-icons/faFileChartPie';
import { faChartMixed } from '@fortawesome/pro-regular-svg-icons/faChartMixed';
import { faChartLine } from '@fortawesome/pro-regular-svg-icons/faChartLine';
import { ApprovalsIcon } from 'components/navigation/components/ApprovalsIcon';

export enum NavigationItemKey {
  Dashboard = 'dashboard',
  Posts = 'posts',
  PostsCalendar = 'postsCalendar',
  PostsList = 'postsList',
  PostsGrid = 'postsGrid',
  PostsBoard = 'postsBoard',
  Albums = 'albums',
  AnalyticsNew = 'analytics-new',
  Insights = 'insights',
  InsightsPost = 'insightsPost',
  InsightsPage = 'insightsPage',
  InsightsCompletion = 'insightsCompletion',
  InsightsPerformanceHub = 'insightsPerformanceHub',
  InsightsLite = 'insights-lite',
  AIContent = 'ai-content',
  BrandHub = 'brand-hub',
  BioPages = 'bio-pages',
  NewReport = 'new-report',
  MyReports = 'my-reports',
  Approvals = 'approvals',
}

export type NavigationItem = {
  label: ReactNode;
  mobileLabel?: ReactNode;
  url?: string;
  icon?: IconDefinition | ReactNode;
  key: NavigationItemKey;
  isRequestable?: boolean;
  routerUrl?: string;
  featureTitle?: string;
  permissionsScope?: UserPermission | UserPermission[];
  onClick?: () => void;
  items?: NavigationItem[];
  isPageSubmenu?: boolean;
  isDisabled?: boolean;
};

type GetNavigationItems = {
  account: AccountStore;
  user: User;
  insightSelectedPageType?: number;
};

export const getNavigationItems = ({
  account,
  user,
  insightSelectedPageType,
}: GetNavigationItems): NavigationItem[] => {
  const featuresPermissions = account.featuresPermissions;

  const newAnalytics = featuresPermissions[NEW_ANALYTICS];

  return [
    {
      label: 'Home',
      routerUrl: routes.DASHBOARD,
      key: NavigationItemKey.Dashboard,
      icon: faHouse,
      permissionsScope: [
        UserPermission.ACCESS_DASHBOARD,
        UserPermission.ACCESS_MY_TASKS,
      ],
    },
    {
      label:
        user.role === 'client'
          ? i18n.t('navigation.views.plan')
          : i18n.t('navigation.views.calendar'),
      mobileLabel:
        user.role === 'client'
          ? i18n.t('navigation.views.plan')
          : i18n.t('navigation.views.calendar'),
      routerUrl: routes.POSTS,
      key: NavigationItemKey.Posts,
      icon: faCalendar,
      permissionsScope: UserPermission.ACCESS_CALENDAR,
      items: [
        {
          label: i18n.t('navigation.views.calendar'),
          routerUrl: routes.POSTS_CALENDAR,
          key: NavigationItemKey.PostsCalendar,
          icon: faCalendar,
          permissionsScope: UserPermission.ACCESS_CALENDAR,
        },
        {
          label: i18n.t('navigation.views.list'),
          routerUrl: routes.POSTS_LIST,
          key: NavigationItemKey.PostsList,
          icon: faTable,
          permissionsScope: UserPermission.ACCESS_CALENDAR,
        },
        {
          label: i18n.t('navigation.views.grid'),
          routerUrl: routes.POSTS_GRID,
          key: NavigationItemKey.PostsGrid,
          icon: faGrid2,
          permissionsScope: UserPermission.ACCESS_CALENDAR,
        },
        {
          label: i18n.t('navigation.views.board'),
          routerUrl: routes.POSTS_KANBAN,
          key: NavigationItemKey.PostsBoard,
          icon: faChartTreeMap,
          permissionsScope: UserPermission.ACCESS_CALENDAR,
        },
      ],
    },

    {
      label: i18n.t('navigation.approvals'),
      routerUrl: routes.POSTS_APPROVALS,
      key: NavigationItemKey.Approvals,
      icon: <ApprovalsIcon />,
      permissionsScope: [
        UserPermission.POST_COMMUNICATION_CLIENT_SHOW,
        UserPermission.POST_COMMUNICATION_INTERNAL_SHOW,
      ],
    },
    {
      label: i18n.t('navigation.brandHub'),
      routerUrl: routes.BRAND_HUB,
      key: NavigationItemKey.BrandHub,
      icon: faSwatchbook,
      permissionsScope: UserPermission.POST_CREATE,
    },
    {
      label: i18n.t('navigation.analytics.new'),
      mobileLabel: i18n.t('navigation.analytics.new'),
      routerUrl: routes.ANALYTICS_OVERVIEW,
      key: NavigationItemKey.AnalyticsNew,
      icon: faChartLine,
      isRequestable: !newAnalytics?.hasAccess,
      featureTitle: newAnalytics?.title,
      permissionsScope: UserPermission.ACCESS_ANALYTICS,
    },
    {
      label: i18n.t('navigation.analytics.default'),
      routerUrl: routes.INSIGHTS,
      key: NavigationItemKey.Insights,
      icon: faChartPieSimple,
      isRequestable: !newAnalytics?.hasAccess,
      featureTitle: newAnalytics?.title,
      permissionsScope: UserPermission.ACCESS_ANALYTICS,
      items: [
        {
          label: i18n.t('navigation.analytics.performanceHub'),
          routerUrl: routes.INSIGHTS_PERFORMANCE_HUB,
          key: NavigationItemKey.InsightsPerformanceHub,
          icon: faChartMixed,
          isRequestable: !newAnalytics?.hasAccess,
          featureTitle: newAnalytics?.title,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
          isDisabled: insightSelectedPageType
            ? isViewRestrictedForPageType(
                routes.INSIGHTS_PERFORMANCE_HUB,
                insightSelectedPageType,
              )
            : false,
        },
        {
          label: i18n.t('navigation.analytics.post'),
          routerUrl: routes.INSIGHTS_POSTS_PERFORMANCE,
          key: NavigationItemKey.InsightsPost,
          icon: faGrid2,
          isRequestable: !newAnalytics?.hasAccess,
          featureTitle: newAnalytics?.title,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
          isDisabled: insightSelectedPageType
            ? isViewRestrictedForPageType(
                routes.INSIGHTS_POSTS_PERFORMANCE,
                insightSelectedPageType,
              )
            : false,
        },
        {
          label: i18n.t('navigation.analytics.page'),
          routerUrl: routes.INSIGHTS_PAGE_PERFORMANCE,
          key: NavigationItemKey.InsightsPage,
          icon: faFlag,
          isRequestable: !newAnalytics?.hasAccess,
          featureTitle: newAnalytics?.title,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
          isDisabled: insightSelectedPageType
            ? isViewRestrictedForPageType(
                routes.INSIGHTS_PAGE_PERFORMANCE,
                insightSelectedPageType,
              )
            : false,
        },
        {
          label: i18n.t('navigation.analytics.competition'),
          routerUrl: routes.INSIGHTS_COMPETITION_ANALYSIS,
          key: NavigationItemKey.InsightsCompletion,
          icon: faChartPieSimple,
          isRequestable: !newAnalytics?.hasAccess,
          featureTitle: newAnalytics?.title,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
          isDisabled: insightSelectedPageType
            ? isViewRestrictedForPageType(
                routes.INSIGHTS_COMPETITION_ANALYSIS,
                insightSelectedPageType,
              )
            : false,
        },
      ],
    },
    {
      label: 'Analytics Lite',
      routerUrl: routes.INSIGHTS_LITE,
      key: NavigationItemKey.InsightsLite,
      icon: faChartSimple,
      permissionsScope: UserPermission.ACCESS_CALENDAR,
    },
    {
      label: i18n.t('navigation.mediaLibrary'),
      url: OldApp.URL.MEDIA_LIBRARY,
      key: NavigationItemKey.Albums,
      icon: faFolderImage,
      permissionsScope: UserPermission.ACCESS_MEDIA_LIBRARY,
    },

    {
      label: i18n.t('navigation.reports.default'),
      routerUrl: routes.REPORTS,
      key: NavigationItemKey.MyReports,
      icon: faFileChartPie,
      permissionsScope: UserPermission.ACCESS_ANALYTICS,
      items: [
        {
          label: i18n.t('navigation.reports.new'),
          routerUrl: routes.NEW_REPORT,
          key: NavigationItemKey.NewReport,
          icon: faFilePlus,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
        },
        {
          label: i18n.t('navigation.reports.my'),
          routerUrl: routes.MY_REPORTS,
          key: NavigationItemKey.MyReports,
          icon: faFiles,
          permissionsScope: UserPermission.ACCESS_ANALYTICS,
        },
      ],
    },
    {
      label: i18n.t('navigation.bioPages'),
      routerUrl: routes.BIO,
      key: NavigationItemKey.BioPages,
      icon: faLink,
      permissionsScope: UserPermission.POST_CREATE,
    },
  ]
    .filter((item) => {
      return !(
        item.key === NavigationItemKey.InsightsLite &&
        !isIncludedInInsightsLiteExperiment({ user })
      );
    })
    .filter((item) => {
      return !(
        [NavigationItemKey.Albums, NavigationItemKey.InsightsLite].includes(
          item.key,
        ) && account.isTrial
      );
    })
    .filter((item) => {
      return !(
        item.key === NavigationItemKey.Approvals &&
        !['client', 'admin', 'manager'].includes(user.role)
      );
    })
    .filter((item) => {
      return !(
        item.key === NavigationItemKey.BrandHub && user.role === 'client'
      );
    })
    .filter((item) => {
      return !(
        item.key === NavigationItemKey.AnalyticsNew &&
        !canAccessNewAnalytics(account.publicId, user.role, account.accountId)
      );
    });
};
